import React, { useState, useEffect, useRef } from 'react';
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { API } from '../../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import clean from '../../utils/Clean';
import { addMixPanelEvent } from '../../store/actions';
import { PUBLISH_PLAYER_CLICKED } from '../../constants/mixpanel-events';
import { Card, PageHeader } from '../../components/ui/bootstrap';
import { Modal } from 'react-bootstrap';

import downChevron from '../../assets/down_chevron.svg';
import { Dropdown, DropdownItem } from '../../components/ui/bootstrap';
import ExtraAudiosUpload from '../../components/integration/ExtraAudiosUpload';
import AudioPlayer from '../../components/AudioPlayer';
import PlayerPreview from '../../components/PlayerPreview';
import Spinner from '../../components/Spinner';
import PlayerSkeleton from '../../components/skeletons/pages/PlayerSkeleton';
import ColorPicker from '../../components/ColorPicker';
import * as Sentry from '@sentry/react';
import { useIntegration, useUpdateIntegrationPlayer } from '../../http-hooks/integration/useIntegration';
import { useOrganisation } from '../../http-hooks/organisation/useOrganisation';
const classNames = require('classnames');

function Player(){

    const { org_id, id } = useParams();
    const dispatch = useDispatch();

    // ✨ Migrated to React Query
    const { data: integration, isLoading } = useIntegration({ org_id, integration_id: id });
    const { data: organization } = useOrganisation({ org_id });
    const { mutateAsync: updatePlayer, isPending: isUpdating } = useUpdateIntegrationPlayer({ org_id, integration_id: id });
    
    const [audio, setAudio] = useState(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const { register, watch, reset, getValues, setValue, formState: { errors }, handleSubmit } = useForm();
    const [ playerType, setPlayerType ] = useState("inline");
    const [ playerPosition, setPlayerPosition ] = useState("default");
    const watchAll = watch();
    const [show, setShow] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [savedSettings, setSavedSettings] = useState([]);
    const [draftName, setDraftName] = useState("");
    const [draftNameError, setDraftNameError] = useState(null);
    const [isFetchingPublishedSetting, setIsFetchingPublishedSetting] = useState(false);
    const [currentDraftName, setCurrentDraftName] = useState("")
    const [deleteDraftId, setDeleteDraftId] = useState(null)
    const [isDeletingDraft, setIsDeletingDraft] = useState(false)

    const draftNameRef = useRef(null)

    const openSaveDraftModal = () => {
        setShow(true)
    }

    const handleSaveDraftEnterPress = (event) => {
        if(event.key === 'Enter'){
            onSaveDraft()
        }
    }

    const onSubmit = async (data) => {
        dispatch(addMixPanelEvent({
            orgId: org_id, integrationId: id, body: {
                eventName: PUBLISH_PLAYER_CLICKED,
                eventData: { player_type: playerType, player_position: playerPosition },
            },
        }));

        data = clean(data);
        data = {...integration.audio_player_settings, ...data, player_position : playerPosition, player_type : playerType}

        try {
            await updatePlayer(clean(data));
            setShowConfirmation(false);
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    const onPlayerTypeChange = (event) => {
        const newPlayerType = event.target.value
        setPlayerType(newPlayerType)
        if(newPlayerType === "floating"){
            setPlayerPosition("bottom-right")
        }else{
            setPlayerPosition("default")   
        }

    }

    // ✨ getIntegration and getOrganization removed - using React Query hooks

    const getSettings = (id) => {

        API.get(`main`, `/organisation/${org_id}/integration/${id}/player_setting`).then((response)=>{

            setSavedSettings(response.data);

        });

    }

    const onSaveDraft = () => {

        if(draftName.length === 0){
            setDraftNameError("Name is required");
            return;
        }

        let data = clean(getValues());
        data.setting_name = draftName;

        setIsSavingDraft(true);

        API.post(`main`, `/organisation/${org_id}/integration/${id}/player_setting`,{body: clean(data), headers: {"Content-Type":"application/json"}})
            .then((response)=>{

            toast.success("Player settings have been saved as draft");
            getSettings(id);
            setShow(false);
            setDraftName("");

        }).catch((error)=>{

            toast.error("Something went wrong while saving the draft.");
            Sentry.captureException(error);

        }).finally(()=>{

            setIsSavingDraft(false);

        });

    }

    const onDeleteDraft = () => {
        const setting_id = deleteDraftId

        setIsDeletingDraft(true)
        API.del(`main`, `/organisation/${org_id}/integration/${id}/player_setting/${setting_id}`, { headers: {"Content-Type":"application/json"}})
            .then(()=>{

                setSavedSettings(savedSettings.filter(item => item.id !== setting_id));
                toast.success("Draft has been deleted.");

            }).catch((error)=>{

                toast.error("Something went wrong in deleting the draft.");
                Sentry.captureException(error);

            }).finally(() => {
                setIsDeletingDraft(false)
                setDeleteDraftId(null)
            });

    }

    const onResetPublishSetting = () => {
        setCurrentDraftName("");
        setIsFetchingPublishedSetting(true);

        if (integration) {
            reset(integration.audio_player_settings);
            setPlayerType(integration.audio_player_settings.player_type);
            toast.success("Successfully restored the last published setting.");
        }

        setIsFetchingPublishedSetting(false);
    }

    const restoreFromDraft = (setting) => {

        reset(setting);

        setCurrentDraftName(setting.setting_name)
        toast.success("Restored player setting from draft.");

    }

    const handleDeleteDraft = (id) => {
        setDeleteDraftId(id)
    }




    useEffect(() => {
        if(show){
            draftNameRef.current.focus()
        }
    }, [show])

    useEffect(() => {
        // ✨ integration and organization now loaded via React Query hooks
        if (integration) {
            reset(integration.audio_player_settings);
            setPlayerType(integration.audio_player_settings.player_type);
            setPlayerPosition(integration.audio_player_settings.player_position);
        }

        getSettings(id);
    }, [integration, id, reset]);


    return(
        <React.Fragment>
            <PageHeader title="Audio Player" subtitle="Configure how the audio player appears on your website or blog when you integrate Listen2It" />
            {
                ( savedSettings.length > 0 ) &&
                <>
                <Dropdown
                    trigger={
                        <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2 my-3">
                            { currentDraftName.length ? currentDraftName : "Saved Drafts" }
                            <img src={downChevron} alt="down chevron" style={{ width: 14 }} />
                        </button>
                    }
                    align="start"
                >
                    {savedSettings.map((setting) => (
                        <DropdownItem
                            key={setting.id}
                            onClick={() => restoreFromDraft(setting)}
                        >
                            <span className="text-truncate flex-grow-1" style={{ maxWidth: 140 }}>{setting.setting_name}</span>
                            <svg
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                height="14px"
                                onClick={(e) => { e.stopPropagation(); handleDeleteDraft(setting.id); }}
                                fill="none"
                                stroke="var(--bs-danger)"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </DropdownItem>
                    ))}
                </Dropdown>
                {
                    (isFetchingPublishedSetting)?
                    (
                        <div className="d-inline-block ms-sm-3">
                            <Spinner color="var(--bs-secondary-color)" />
                        </div>
                    )
                    :
                    (
                        <span onClick={()=>{onResetPublishSetting()}} className="d-inline-block ms-sm-3 mb-3 mb-sm-0 text-muted" style={{borderBottom: '1px dashed var(--bs-border-color)', cursor: 'pointer'}}>Restore to published settings</span>
                    )
                }
                </>
            }
            {
                (isLoading)?
                (
                    <PlayerSkeleton />
                )
                :
                (
                    <div className="row">
                        <div className="col-xl-6 order-2 order-xl-1">
                            <form>
                                <div className="d-flex flex-column gap-4">

                                <Card className="p-4" shadow>
                                    <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Basic Settings</p>
                                    <div className="mb-3">
                                        <label>Audio Player Type</label>
                                        <select className={classNames("form-select", { "is-invalid": errors.player_type})}  {...register("player_type", {required: true})} onChange={onPlayerTypeChange}>
                                            <option value="inline">Inline</option>
                                            <option value="static">Static</option>
                                            <option value="floating">Floating</option>
                                        </select>
                                        {
                                            errors.player_type &&
                                            <div className="invalid-feedback">
                                                {
                                                    ("required"===errors.player_type.type) && "Language selection is required."
                                                }
                                            </div>
                                        }
                                        {
                                            ("inline" === playerType) &&
                                            <small className="form-text text-muted">The player will appear embedded in the content of the page.</small>
                                        }
                                        {

                                            ("static" === playerType) &&
                                            <small className="form-text text-muted">The player will appear always on top stuck to the bottom edge of the screen.</small>
                                        }
                                        {

                                            ("floating" === playerType) &&
                                            <small className="form-text text-muted">The player will appear as a minimized button at selected position.</small>
                                        }
                                    </div>
                                    <div className={classNames("mb-3", {"d-none": "floating" !== playerType})}>
                                        <label>Player Position</label>
                                        <select value={playerPosition} onChange={(e) => setPlayerPosition(e.target.value)} className={classNames("form-select", { "is-invalid": errors.player_position})} name="player_position">
                                            <option value="bottom-right">Bottom Right</option>
                                            <option value="bottom-left">Bottom Left</option>
                                        </select>
                                        {
                                            errors.player_position &&
                                            <div className="invalid-feedback">
                                                {
                                                    ("required"===errors.player_position.type) && "Language selection is required."
                                                }
                                            </div>
                                        }
                                    </div>
                                </Card>

                                <Card className="p-4" shadow>
                                    <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Player Settings</p>
                                    <div className="mb-3">
                                        <label>Player Color</label>
                                        <div className="input-group mb-3">
                                            <span className="input-group-text">
                                                <ColorPicker defaultColor={getValues('color')} onChange={(color)=>{setValue('color',color);}}/>
                                            </span>
                                            <input type="text" className="form-control"  {...register("color")}/>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label>Player Text Color</label>
                                        <div className="input-group mb-3">
                                            <span className="input-group-text">
                                                <ColorPicker defaultColor={getValues('text_color')} onChange={(color)=>{setValue('text_color',color);}}/>
                                            </span>
                                            <input type="text" className="form-control"  {...register("text_color")} />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label>Player Background Color</label>
                                        <div className="input-group mb-3">
                                            <span className="input-group-text">
                                                <ColorPicker defaultColor={getValues('background_color')} onChange={(color)=>{setValue('background_color',color);}}/>
                                            </span>
                                            <input type="text" className="form-control"  {...register("background_color")} />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label>Player Title</label>
                                        <input type="text" className="form-control"  {...register("title")} />
                                    </div>
                                </Card>

                                <Card className="p-4" shadow>
                                    <p className="fw-semibold mb-3 text-body-emphasis" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Other Settings</p>
                                    <div className="mb-3 d-flex flex-row justify-content-start align-items-center">
                                        <input className="me-2" type="checkbox"  {...register("download_button")} />
                                        <p className="m-0 font-bold">Download Audio Button</p>
                                    </div>
                                    <div className="mb-3 d-flex flex-row justify-content-start align-items-center">
                                        <input className="me-2" type="checkbox" disabled={(false === integration.gates.can_remove_widget_branding)}  {...register("remove_branding")} />
                                        <p className="m-0 font-bold">Remove Listen2It Branding </p>
                                        {
                                            (false === integration.gates.can_remove_widget_branding) &&
                                            <Link
                                                to={`/organisation/${org_id}/upgrade?source=remove_branding_link`}
                                                className="ms-2 fst-italic text-decoration-none"
                                            >
                                                Upgrade
                                            </Link>
                                        }
                                    </div>
                                </Card>

                                </div>
                                
                                {
                                    organization && organization.gates.can_access_extra_audios ? <>
                                        <ExtraAudiosUpload orgId={org_id} integrationId={id} integration={integration} onPlay={(audio) => {
                                            setAudio(() => null);
                                            setAudio(() => `${process.env.REACT_APP_AUDIO_BUCKET}/${audio}`)
                                        }} />
                                        {
                                            (audio) &&
                                            <div className="audio-player-wrapper">
                                                <AudioPlayer onClose={() => setAudio(null)} audio={audio} title={""} />
                                            </div>
                                        }
                                    </> : null
                                }
                                
                                

                                <div className="row mt-4">
                                    <div className="col-12 text-end">
                                        <Link to={`/organisation/${org_id}/integration/${id}/content`}>Cancel</Link>
                                        <button type="button" className="btn btn-outline-primary ms-3 me-3" onClick={openSaveDraftModal}>Save as Draft</button>
                                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowConfirmation(true)}>Publish Changes</button>
                                        
                                        <Modal show={show} onHide={()=>{setShow(false)}}>
                                            <Modal.Header closeButton>
                                                <Modal.Title>Save as Draft</Modal.Title>
                                            </Modal.Header>
                                            <Modal.Body>
                                                    <div>
                                                        <div className="mb-3">
                                                            <label>Give it a name</label>
                                                            <input ref={draftNameRef} type="text" className={classNames("form-control", { "is-invalid": draftNameError })} value={draftName} onChange={(event)=>{setDraftNameError(null);setDraftName(event.target.value)}} onKeyPress={(event) => handleSaveDraftEnterPress(event)} />
                                                            {
                                                                (draftNameError) &&
                                                                <div className="invalid-feedback">
                                                                    {draftNameError}
                                                                </div>
                                                            }
                                                        </div>
                                                    </div>
                                            </Modal.Body>
                                            <Modal.Footer>
                                                <button type="button" className="btn btn-outline-primary me-3" onClick={()=>setShow(false)}>Cancel</button>
                                                {
                                                    (isSavingDraft)?
                                                    (
                                                        <button type="button" className="btn btn-primary">
                                                            <Spinner color="white" />
                                                        </button>
                                                    )
                                                    :
                                                    (
                                                        <button type="button" className="btn btn-primary" onClick={onSaveDraft}>Save</button>
                                                    )
                                                }
                                            </Modal.Footer>
                                        </Modal>

                                        <Modal show={showConfirmation} onHide={()=>{setShowConfirmation(false)}}>
                                            <Modal.Header closeButton>
                                                <Modal.Title>Publish Changes</Modal.Title>
                                            </Modal.Header>
                                            <Modal.Body>          
                                                <p style={{fontWeight : 500}}>Are you sure you want to publish the new player settings?</p>
                                                <p>The new player settings would go live on all pages where the Listen2It player of this integration has been embedded.</p>
                                            </Modal.Body>
                                            <Modal.Footer>
                                                <button type="button" className="btn btn-outline-primary me-3" onClick={()=>setShowConfirmation(false)}>No&nbsp;</button>
                                                {
                                                    (isUpdating)?
                                                    (
                                                        <button type="button" className="btn btn-primary">
                                                            <Spinner color="white" />
                                                        </button>
                                                    )
                                                    :
                                                    (
                                                        <button type="button" onClick={handleSubmit(onSubmit)} className="btn btn-primary">Yes</button>
                                                    )
                                                }
                                            </Modal.Footer>
                                        </Modal>

                                        <Modal show={deleteDraftId} onHide={()=>{setDeleteDraftId(null)}}>
                                            <Modal.Header closeButton>
                                                <Modal.Title>Delete Draft</Modal.Title>
                                            </Modal.Header>
                                            <Modal.Body>          
                                                <p>Are you sure you want to delete this draft?</p>
                                            </Modal.Body>
                                            <Modal.Footer>
                                                <button type="button" className="btn btn-outline-primary me-3" onClick={()=>setDeleteDraftId(null)}>No&nbsp;</button>
                                                {
                                                    (isDeletingDraft)?
                                                    (
                                                        <button type="button" className="btn btn-primary">
                                                            <Spinner color="white" />
                                                        </button>
                                                    )
                                                    :
                                                    (
                                                        <button type="button" onClick={onDeleteDraft} className="btn btn-primary">Yes</button>
                                                    )
                                                }
                                            </Modal.Footer>
                                        </Modal>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="col-xl-4 mb-4 order-1 order-xl-2">
                            <PlayerPreview type={playerType} color={watchAll.color} position={playerPosition} title={watchAll.title} text_color={watchAll.text_color} background_color={watchAll.background_color} />
                        </div>
                    </div>
                )
            }
        </React.Fragment>
    );
}

export default Player;
